import { Modal } from "@mui/material";
import { isBlank, isNullOrUndefined } from "../../utils";
import { ReactComponent as Close } from "../../assets/close-white-circle.svg";
import { ReactComponent as ArrowLeft } from "../../assets/arrow-left-circle-fill.svg";
import { ReactComponent as ArrowRight } from "../../assets/arrow-right-circle-fill.svg";
import { ReactComponent as PlayIcon } from "../../assets/play-circle-fill.svg";
import { ReactComponent as PauseIcon } from "../../assets/pause-circle-fill.svg";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getSignedImageSrc } from "../../mediaUtils";

function ImageAssetModal(props) {
  const { t } = useTranslation();
  const { imageAsset, onClose, onNext, onPrev, showNavigation } = props;
  const [imageAssetToRender, setImageAssetToRender] = useState(null);
  const [isAutoPlay, setIsAutoPlay] = useState(imageAsset.autoplay);
  const [progressStyle, setProgressStyle] = useState({});
  const [currentImageUuid, setCurrentImageUuid] = useState(null);
  const [nextImageRequested, setNextImageRequested] = useState(false);
  const animationRequest = useRef(null);

  const imageAssetUrl = !isNullOrUndefined(imageAsset)
    ? imageAsset.fullSize
    : null;
  const hasImage = !isNullOrUndefined(imageAssetUrl);
  // Scale image to a specific width
  const signedImageSrc = hasImage ? getSignedImageSrc(imageAssetUrl) : "";

  const imageCaption = imageAsset.caption;
  const hasCaption = !isBlank(imageCaption);
  const imageNotes = imageAsset.notes;
  const hasImageNotes = !isBlank(imageNotes);
  const downloadLinkRef = useRef(null);

  function handleImageLoaded(e) {
    const imageEl = e.target;
    // Use imgRef to measure the dimensions
    const width = imageEl.getBoundingClientRect().width;
    const height = imageEl.getBoundingClientRect().height;

    const modalStyle = {
      backgroundImage: !isBlank(signedImageSrc) ? `url(${signedImageSrc})` : "",
      minWidth: "unset",
    };
    let ratio = 1;
    if (width > height) {
      ratio = (window.innerWidth * 0.8) / width;
      modalStyle.width = `${width * ratio}px`;
      modalStyle.height = `${height * ratio}px`;
      if (height * ratio > window.innerHeight * 0.8) {
        // additional adjustment needed
        ratio = (window.innerHeight * 0.8) / height;
        modalStyle.height = `${height * ratio}px`;
        modalStyle.width = `${width * ratio}px`;
      }
    } else {
      ratio = (window.innerHeight * 0.8) / height;
      modalStyle.height = `${height * ratio}px`;
      modalStyle.width = `${width * ratio}px`;
    }
    setImageAssetToRender({
      modalStyle: modalStyle,
    });
  }

  useEffect(() => {
    const imageAssetUrl = !isNullOrUndefined(imageAsset)
      ? imageAsset.fullSize
      : null;

    if (isNullOrUndefined(imageAssetUrl)) {
      // If no image, set the "notes" modal style
      setImageAssetToRender({
        modalStyle: {
          width: "640px",
          height: "240px",
          minWidth: "unset",
          backgroundImage: "",
        },
      });
    }
  }, [imageAsset]);

  // Effect to handle navigation to the next image during autoplay
  useEffect(() => {
    if (isAutoPlay && nextImageRequested) {
      setNextImageRequested(false);
      onNext();
    }
  }, [onNext, isAutoPlay, nextImageRequested]);

  // Effect to handle initiating countdown for autoplay
  useEffect(() => {
    if (isAutoPlay && currentImageUuid !== imageAsset.uuid) {
      // Create the animation function and execute it asynchronously
      (async () => {
        let startTime = null;
        function animationStep(timestamp) {
          if (!startTime) startTime = timestamp;
          const progress = Math.min(
            (timestamp - startTime) / 5000, // Countdown duration 5 secs
            1
          );
          setProgressStyle({
            width: `${progress * 100}%`,
          });
          if (progress < 1) {
            animationRequest.current = requestAnimationFrame(animationStep);
          } else if (progress >= 1) {
            setNextImageRequested(true);
          }
        }
        animationRequest.current = requestAnimationFrame(animationStep);
      })();
      setCurrentImageUuid(imageAsset.uuid);
    }
  }, [currentImageUuid, isAutoPlay, imageAsset]);

  function stopAutoplay() {
    setIsAutoPlay(false);
    setCurrentImageUuid(null);
    if (!isNullOrUndefined(animationRequest.current)) {
      cancelAnimationFrame(animationRequest.current);
    }
  }
  function handleClose() {
    stopAutoplay();
    onClose();
  }

  function handlePlayToggle() {
    if (isAutoPlay) {
      stopAutoplay();
    } else {
      setIsAutoPlay(true);
    }
  }

  return (
    <div>
      <Modal
        open={!isNullOrUndefined(imageAssetToRender) || hasImageNotes}
        onClose={handleClose}
        sx={{
          "& .MuiBackdrop-root": {
            backdropFilter: "blur(2px)",
          },
        }}
      >
        <div
          className="modal image-asset-modal"
          style={imageAssetToRender?.modalStyle}
        >
          <div className="image-asset-modal-header">
            {isAutoPlay && (
              <div className="countdown-progress">
                <div style={progressStyle}></div>
              </div>
            )}
            <div>
              <div>
                {showNavigation && (
                  <div className="autoplay-button" onClick={handlePlayToggle}>
                    {!isAutoPlay ? <PlayIcon /> : <PauseIcon />}
                  </div>
                )}
              </div>
              <div>
                {hasImage && (
                  <>
                    <button
                      className="white"
                      onClick={() => {
                        // Note: file must be same origin to automatically download (this opens new tab)
                        downloadLinkRef.current.click();
                      }}
                    >
                      {t("download")}
                    </button>
                    <a
                      href={signedImageSrc}
                      className="not-visible"
                      ref={downloadLinkRef}
                      target="_blank"
                      rel="noreferrer"
                      download={`image.jpg`}
                    >
                      {signedImageSrc}
                    </a>
                  </>
                )}
              </div>
              <div className="close-button" onClick={handleClose}>
                <Close />
              </div>
            </div>
          </div>
          <div className="image-asset-modal-body">
            {showNavigation && (
              <div className="image-asset-modal-navigation">
                <div>
                  <div
                    className="navigation-button left"
                    onClick={() => {
                      stopAutoplay();
                      onPrev();
                    }}
                  >
                    <ArrowLeft />
                  </div>
                </div>
                <div className="image-asset-notes">
                  {hasImageNotes && !hasImage && <div>{imageNotes}</div>}
                </div>
                <div>
                  <div
                    className="navigation-button right"
                    onClick={() => {
                      stopAutoplay();
                      onNext();
                    }}
                  >
                    <ArrowRight />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="image-asset-modal-footer">
            {hasCaption && (
              <div className="caption">
                <div>{imageCaption}</div>
              </div>
            )}
          </div>
        </div>
      </Modal>
      <div className="hideout">
        <img
          src={signedImageSrc}
          onLoad={(e) => {
            handleImageLoaded(e);
          }}
          alt=""
        />
      </div>
    </div>
  );
}

export default ImageAssetModal;
